import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  BookOpen,
  ChevronDown,
  CreditCard,
  Home as HomeIcon,
  LifeBuoy,
  PackageOpen,
  ShoppingBag,
  Store,
  UserCog,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { useGroupOverview } from '@/hooks/useGroupOverview';
import { AccountSwitcher } from './AccountSwitcher';
import { ContactUsMenu } from './ContactUsMenu';

/**
 * A leaf nav entry. `to` is the exact existing route it links to. When `soon`
 * is set the entry has no screen yet — it renders disabled with a "Soon" pill
 * and is not navigable.
 */
interface NavLeaf {
  label: string;
  to?: string;
  soon?: boolean;
  /**
   * Extra pathnames (exact or prefix) that should also mark this leaf active.
   * Used by Order history, which lives at /order?tab=history (same path as
   * New order) — we disambiguate on the `?tab` query in `useLeafActive`.
   */
  matchSearch?: string;
}

/**
 * A top-level sidebar section. A section with `children` is an expandable
 * group (a toggle button); a section with `to` and no children is a single
 * direct link (e.g. Home, Service).
 */
interface NavSection {
  key: string;
  label: string;
  icon: LucideIcon;
  to?: string;
  children?: NavLeaf[];
}

// Approved grouping. Single-item sections (Home, Service) link directly;
// grouped sections expand to reveal their screens. Settings / Help / Admin
// continue to live in the top-right account menu.
const SECTIONS: NavSection[] = [
  { key: 'home', label: 'Home', icon: HomeIcon, to: '/home' },
  {
    key: 'orders',
    label: 'Orders',
    icon: ShoppingBag,
    children: [
      { label: 'New order', to: '/order' },
      { label: 'Order history', to: '/order?tab=history', matchSearch: 'tab=history' },
      { label: 'My Collection', to: '/collection' },
    ],
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: CreditCard,
    children: [
      { label: 'Invoices', to: '/invoices' },
      { label: 'Statements', to: '/statements' },
      { label: 'Pay', to: '/pay' },
      { label: 'Payments & credits', to: '/payments' },
    ],
  },
  { key: 'service', label: 'Service', icon: LifeBuoy, to: '/service' },
  { key: 'resources', label: 'Resources', icon: BookOpen, to: '/resources' },
  {
    key: 'equipment',
    label: 'Equipment',
    icon: PackageOpen,
    children: [
      { label: 'Equipment rentals', to: '/equipment' },
      { label: 'CO₂ cylinders', to: '/cylinders' },
    ],
  },
  {
    key: 'account',
    label: 'Account',
    icon: UserCog,
    children: [
      { label: 'Profile', to: '/settings/profile' },
      { label: 'Users', to: '/settings/team' },
      { label: 'Payment methods', to: '/settings/payments' },
      { label: 'Autopay', to: '/autopay' },
      // Contracts & files live inside the Profile tab; deep-link to that card.
      // (There is no standalone "Locations" screen — locations are the
      // sub-accounts under a master account, managed via the account model.)
      { label: 'Contracts', to: '/settings/profile#contracts' },
    ],
  },
];

/** Paths that have a query-disambiguated sibling (e.g. /order: New vs History). */
const TAB_SPLIT_PATHS = new Set(
  SECTIONS.flatMap((s) => s.children ?? [])
    .filter((l) => l.matchSearch)
    .map((l) => l.to!.split('?')[0]),
);

/** Does the current location match a leaf's target (path + optional ?tab)? */
function useLeafActive() {
  const { pathname, search } = useLocation();
  return (leaf: NavLeaf): boolean => {
    if (!leaf.to) return false;
    const leafPath = leaf.to.split('?')[0];
    const pathMatch = pathname === leafPath || pathname.startsWith(`${leafPath}/`);
    if (!pathMatch) return false;
    // Same-path leaves (New order vs Order history) disambiguate on the query.
    if (leaf.matchSearch) return search.includes(leaf.matchSearch);
    // A plain leaf whose path also has a ?tab sibling (e.g. New order at /order)
    // is only active when no competing tab is present.
    if (TAB_SPLIT_PATHS.has(leafPath)) return !/[?&]tab=/.test(search);
    return true;
  };
}

export function Sidebar() {
  const { pathname, search } = useLocation();
  const leafActive = useLeafActive();

  // Chain / franchise rollup entry — only for users whose group master
  // gives them multiple store accounts (e.g. a franchisee's AP person).
  const { data: groupData } = useGroupOverview();
  const hasStoreGroups = (groupData?.groups?.length ?? 0) > 0;

  // Which grouped section currently contains the active route?
  const activeSectionKey = useMemo(() => {
    for (const s of SECTIONS) {
      if (!s.children) continue;
      if (s.children.some((leaf) => leafActive(leaf))) return s.key;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search]);

  // Expanded state per section key. Seed with the active section open.
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    activeSectionKey ? { [activeSectionKey]: true } : {},
  );

  // Auto-expand the section containing the active route as it changes.
  useEffect(() => {
    if (!activeSectionKey) return;
    setExpanded((prev) =>
      prev[activeSectionKey] ? prev : { ...prev, [activeSectionKey]: true },
    );
  }, [activeSectionKey]);

  const toggle = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <aside
      className={cn(
        'w-56 flex flex-col',
        'border-r border-brix-border bg-brix-surface/85 backdrop-blur-xl',
      )}
    >
      <AccountSwitcher variant="sidebar" />

      <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-thin">
        {hasStoreGroups && (
          <NavLink
            to="/stores"
            className={({ isActive }) =>
              cn(
                'group relative w-full flex items-center gap-2.5',
                'px-3 py-2 rounded-lg text-sm mb-0.5 cursor-pointer transition-all duration-200',
                isActive
                  ? 'bg-brix-primary-tint text-brix-primary font-semibold'
                  : 'text-brix-ink font-medium hover:bg-brix-primary-tint/60 hover:translate-x-0.5',
              )
            }
          >
            <Store size={18} strokeWidth={1.75} />
            My stores
          </NavLink>
        )}

        {SECTIONS.map((section) => {
          const Icon = section.icon;

          // Single-item section → direct NavLink.
          if (!section.children) {
            return (
              <NavLink
                key={section.key}
                to={section.to!}
                data-tour={`nav-${section.key}`}
                className={({ isActive }) =>
                  cn(
                    'group relative w-full flex items-center gap-2.5',
                    'px-3 py-2 rounded-lg text-sm mb-0.5 cursor-pointer transition-all duration-200',
                    isActive
                      ? 'bg-brix-primary-tint text-brix-primary font-semibold'
                      : 'text-brix-ink font-medium hover:bg-brix-primary-tint/60 hover:translate-x-0.5',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      aria-hidden
                      className={cn(
                        'absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full transition-all duration-200',
                        isActive ? 'bg-brix-primary opacity-100' : 'opacity-0',
                      )}
                    />
                    <Icon size={18} strokeWidth={isActive ? 2 : 1.75} />
                    {section.label}
                  </>
                )}
              </NavLink>
            );
          }

          // Grouped section → toggle button + collapsible children.
          const open = !!expanded[section.key];
          const sectionActive = activeSectionKey === section.key;
          const panelId = `nav-section-${section.key}`;

          return (
            <div key={section.key} className="mb-0.5">
              <button
                type="button"
                data-tour={`nav-${section.key}`}
                onClick={() => toggle(section.key)}
                aria-expanded={open}
                aria-controls={panelId}
                className={cn(
                  'group relative w-full flex items-center justify-between',
                  'px-3 py-2 rounded-lg text-sm cursor-pointer transition-all duration-200',
                  sectionActive
                    ? 'text-brix-primary font-semibold'
                    : 'text-brix-ink font-medium hover:bg-brix-primary-tint/60',
                )}
              >
                <span className="flex items-center gap-2.5">
                  <Icon size={18} strokeWidth={sectionActive ? 2 : 1.75} />
                  {section.label}
                </span>
                <ChevronDown
                  size={14}
                  className="text-brix-muted transition-transform duration-200"
                  style={{ transform: open ? 'rotate(180deg)' : 'none' }}
                  aria-hidden
                />
              </button>

              {open && (
                <ul id={panelId} className="mt-0.5 mb-1 pl-3.5 space-y-0.5">
                  {section.children.map((leaf) => (
                    <li key={leaf.label}>
                      {leaf.soon ? (
                        <span
                          aria-disabled="true"
                          className="flex items-center justify-between gap-2 pl-3 pr-2 py-1.5 rounded-lg text-sm text-brix-muted/70 cursor-not-allowed border-l border-brix-border"
                        >
                          {leaf.label}
                          <span className="shrink-0 rounded-full bg-brix-primary-tint px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-tight text-brix-primary">
                            Soon
                          </span>
                        </span>
                      ) : (
                        <NavLink
                          to={leaf.to!}
                          className={() =>
                            cn(
                              'block pl-3 pr-2 py-1.5 rounded-lg text-sm transition-all duration-200 cursor-pointer border-l',
                              leafActive(leaf)
                                ? 'bg-brix-primary-tint text-brix-primary font-semibold border-brix-primary'
                                : 'text-brix-ink/90 font-medium border-brix-border hover:bg-brix-primary-tint/60 hover:border-brix-primary/40',
                            )
                          }
                        >
                          {leaf.label}
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-brix-border py-3">
        <ContactUsMenu variant="sidebar" />
      </div>
    </aside>
  );
}
